import type React from "react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ErrorSeverity } from "../../../error-handling/error-handler";
import { errorUI } from "../../../error-handling/index";
import type { MainService } from "../../../main-service";
import { MarkdownExporter } from "../../../summary/markdown-exporter";
import type { Session, SessionSummary } from "../../../types/core";
import { formatFullJapaneseDate } from "../../../utils/date-formatter";
import { renderMarkdown } from "../../../utils/markdown";
import { useSessionActions } from "../../hooks/useSessionActions";
import { UI_ICONS } from "../../icons";

interface SessionDetailViewProps {
	session: Session;
	service: MainService | null;
	currentSessionId?: string | null;
	onBack: () => void;
	onClose?: () => void;
	onSessionUpdate?: (session: Session) => void;
}

/**
 * Formats seconds into m:ss format for playback UI.
 * Performance optimization: Using simple conditional padding instead of toString().padStart(2, "0")
 * to reduce object allocations and method call overhead in the high-frequency playback loop.
 */
const formatPlaybackTime = (seconds: number) => {
	if (Number.isNaN(seconds) || !Number.isFinite(seconds)) return "0:00";
	const m = Math.floor(seconds / 60);
	const s = Math.floor(seconds % 60);
	return `${m}:${s < 10 ? "0" : ""}${s}`;
};

interface TranscriptRowProps {
	segment: Session["transcript"][0];
	isActive: boolean;
	onSeek: (start: number) => void;
	onUpdate: (id: string, text: string) => void;
	onDelete: (id: string) => void;
}

/**
 * Individual transcript row, memoized to prevent re-rendering all rows during playback.
 * This is a critical performance optimization for sessions with many segments.
 */
const TranscriptRow: React.FC<TranscriptRowProps> = memo(
	({ segment, isActive, onSeek, onUpdate, onDelete }) => {
		const safeText = String(segment.text || "");
		const handleCopy = async () => {
			try {
				await navigator.clipboard.writeText(safeText);
				errorUI.showNotification(
					"文字起こしをコピーしました。",
					ErrorSeverity.LOW,
				);
			} catch (err) {
				console.error("Failed to copy transcript segment:", err);
				errorUI.showNotification(
					"コピーに失敗しました。",
					ErrorSeverity.MEDIUM,
				);
			}
		};

		return (
			// biome-ignore lint/a11y/useKeyWithClickEvents: handled by child button/div
			// biome-ignore lint/a11y/noStaticElementInteractions: Entire row is clickable for seek functionality
			<div
				className={`w-full flex items-start gap-4 p-2 rounded-lg transition-all hover:bg-white/5 group cursor-pointer animate-in fade-in slide-in-from-left-5 duration-300 ${isActive ? "bg-primary/10 ring-1 ring-primary/30" : ""}`}
				onClick={() => onSeek(segment.start)}
				data-active={isActive}
			>
				<button
					type="button"
					className={`text-xs font-mono shrink-0 mt-1 cursor-pointer border-none bg-transparent transition-all focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none rounded active:scale-95 ${isActive ? "text-primary font-bold" : "text-text-dim opacity-60 hover:opacity-100"}`}
					title="この位置から再生"
					aria-label={`${formatPlaybackTime(segment.start / 1000)} から再生`}
					onClick={(e) => {
						e.stopPropagation();
						onSeek(segment.start);
					}}
				>
					{formatPlaybackTime(segment.start / 1000)}
				</button>
				{/* biome-ignore lint/a11y/useSemanticElements: div with contentEditable is used for inline editing with custom styling */}
				<div
					className={`flex-1 text-sm leading-relaxed outline-none rounded px-1 transition-all hover:bg-primary/5 focus:bg-white/5 focus:shadow-[0_0_0_1px_rgba(99,102,241,0.3)] ${isActive ? "text-text-main font-medium" : "text-text-dim group-hover:text-text-main"}`}
					role="textbox"
					aria-label={`発言「${safeText.slice(0, 20)}${safeText.length > 20 ? "..." : ""}」を編集`}
					tabIndex={0}
					contentEditable
					suppressContentEditableWarning
					onClick={(e) => e.stopPropagation()}
					onBlur={(e) => {
						const newText = e.target.textContent?.trim() || "";
						if (newText !== safeText && segment.id) {
							onUpdate(segment.id, newText);
						}
					}}
					onKeyDown={(e) => {
						if (e.key === "Enter" && !e.shiftKey) {
							e.preventDefault();
							(e.target as HTMLElement).blur();
						}
						if (e.key === "Escape") {
							e.preventDefault();
							(e.target as HTMLElement).textContent = safeText;
							(e.target as HTMLElement).blur();
						}
					}}
				>
					{safeText}
				</div>
				{segment.id && (
					<div className="flex gap-1 order-last shrink-0">
						<button
							type="button"
							className="opacity-0 group-hover:opacity-100 focus:opacity-100 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none rounded cursor-pointer text-text-dim hover:text-primary transition-all p-1 leading-none active:scale-95 border-none bg-transparent"
							title={`発言「${safeText.slice(0, 20)}${safeText.length > 20 ? "..." : ""}」をコピー`}
							aria-label={`発言「${safeText.slice(0, 20)}${safeText.length > 20 ? "..." : ""}」をコピー`}
							onClick={(e) => {
								e.stopPropagation();
								handleCopy();
							}}
						>
							{UI_ICONS.COPY}
						</button>
						<button
							type="button"
							className="opacity-0 group-hover:opacity-100 focus:opacity-100 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none rounded cursor-pointer text-text-dim hover:text-red-500 transition-all p-1 leading-none active:scale-95 border-none bg-transparent"
							title={`発言「${safeText.slice(0, 20)}${safeText.length > 20 ? "..." : ""}」を削除`}
							aria-label={`発言「${safeText.slice(0, 20)}${safeText.length > 20 ? "..." : ""}」を削除`}
							onClick={(e) => {
								e.stopPropagation();
								if (segment.id) onDelete(segment.id);
							}}
						>
							{UI_ICONS.DELETE}
						</button>
					</div>
				)}
			</div>
		);
	},
);

export const SessionDetailView: React.FC<SessionDetailViewProps> = ({
	session,
	service,
	currentSessionId,
	onBack,
	onClose,
	onSessionUpdate,
}) => {
	const [summary, setSummary] = useState<SessionSummary | null>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(
		(session.metrics?.totalDuration || 0) / 1000,
	);
	const [audioUrl, setAudioUrl] = useState<string | null>(null);
	const [isRegenerating, setIsRegenerating] = useState(false);
	const [transcript, setTranscript] = useState(session.transcript);
	const [isFavorite, setIsFavorite] = useState(!!session.isFavorite);
	const [title, setTitle] = useState(session.title || "");
	const { toggleFavorite } = useSessionActions({ service });
	const sessionRef = useRef(session);
	const transcriptRef = useRef(session.transcript);
	const isFavoriteRef = useRef(!!session.isFavorite);
	const titleRef = useRef(session.title || "");

	const audioRef = useRef<HTMLAudioElement | null>(null);
	const exporterRef = useRef(new MarkdownExporter());
	const transcriptContainerRef = useRef<HTMLDivElement>(null);

	// Performance optimization: Cache the rendered markdown to avoid expensive
	// O(N) regex operations on every audio playback tick (timeUpdate).
	const renderedSummary = useMemo(() => {
		return summary ? renderMarkdown(summary.overallSummary, 2) : "";
	}, [summary]);

	useEffect(() => {
		sessionRef.current = session;
		transcriptRef.current = session.transcript;
		isFavoriteRef.current = Boolean(session.isFavorite);
		titleRef.current = session.title || "";
		setTranscript(session.transcript);
		setIsFavorite(Boolean(session.isFavorite));
		setTitle(session.title || "");
		setDuration((session.metrics?.totalDuration || 0) / 1000);
		setCurrentTime(0);
		setIsPlaying(false);
	}, [session]);

	const notifySessionUpdate = useCallback(
		(patch: Partial<Session>) => {
			if (!onSessionUpdate) return;

			// 親の再描画前でも連続更新を取りこぼさないため、ref を単一の最新ソースとして使う
			if (patch.title !== undefined) titleRef.current = patch.title;
			if (patch.isFavorite !== undefined)
				isFavoriteRef.current = patch.isFavorite;
			if (patch.transcript !== undefined)
				transcriptRef.current = patch.transcript;

			const nextSession = {
				...sessionRef.current,
				title: titleRef.current,
				isFavorite: isFavoriteRef.current,
				transcript: transcriptRef.current,
				...patch,
			};

			sessionRef.current = nextSession;
			onSessionUpdate(nextSession);
		},
		[onSessionUpdate],
	);

	// Load summary and audio on init
	useEffect(() => {
		let isMounted = true;
		let currentUrl: string | null = null;

		// セッション切替時に前回音声の参照を残さない
		setAudioUrl(null);
		setCurrentTime(0);
		if (audioRef.current) {
			audioRef.current.pause();
			audioRef.current.currentTime = 0;
		}

		const loadData = async () => {
			if (service) {
				try {
					// Load Summary
					const s = await service.getSummary(session.id);
					if (isMounted) setSummary(s || null);

					// Load Audio
					const files = await service.getAudioFiles(session.id);
					if (isMounted && files.length > 0) {
						const audioFile = files[0];
						if (audioFile.blob) {
							// Blob の型を保証して URL を作成
							const blob = new Blob([audioFile.blob], {
								type: audioFile.blob.type || "audio/webm",
							});
							currentUrl = URL.createObjectURL(blob);
							if (isMounted) setAudioUrl(currentUrl);
						}
					}
				} catch (err) {
					console.error("Failed to load session details", err);
				}
			}
		};
		loadData();

		return () => {
			isMounted = false;
			if (currentUrl) {
				URL.revokeObjectURL(currentUrl);
			}
		};
	}, [session.id, service]);

	const togglePlay = useCallback(() => {
		if (!audioRef.current || !audioUrl) {
			errorUI.showNotification(
				"音声データが準備できていません。",
				ErrorSeverity.LOW,
			);
			return;
		}

		if (isPlaying) {
			audioRef.current.pause();
		} else {
			const playPromise = audioRef.current.play();
			if (playPromise !== undefined) {
				playPromise.catch((err) => {
					console.error("Playback failed:", err);
					setIsPlaying(false);
					if (err.name === "NotSupportedError") {
						errorUI.showNotification(
							"この音声形式はブラウザで再生できません。",
							ErrorSeverity.MEDIUM,
						);
					}
				});
			}
		}
	}, [audioUrl, isPlaying]);

	const handleSeek = useCallback(
		(start: number) => {
			if (audioRef.current && audioUrl) {
				const targetTime = start / 1000;
				audioRef.current.currentTime = targetTime;
				audioRef.current.play().catch(console.error);
			}
		},
		[audioUrl],
	);

	const handleSkip = useCallback(
		(seconds: number) => {
			if (audioRef.current && audioUrl) {
				const targetTime = Math.max(
					0,
					Math.min(duration, audioRef.current.currentTime + seconds),
				);
				audioRef.current.currentTime = targetTime;
			}
		},
		[audioUrl, duration],
	);

	// Keyboard shortcut for playback control
	useEffect(() => {
		const isEditableOrInteractiveElement = (element: Element | null) => {
			if (!(element instanceof HTMLElement)) return false;
			if (element.isContentEditable) return true;

			// ボタン/リンク/フォーム部品などのネイティブ操作を Space で奪わない
			const interactiveSelector = [
				"input",
				"textarea",
				"select",
				"button",
				"a[href]",
				"summary",
				"[role='button']",
				"[role='link']",
				"[role='checkbox']",
				"[role='radio']",
				"[role='switch']",
				"[role='menuitem']",
				"[role='option']",
				"[role='tab']",
				"[role='slider']",
				"[role='spinbutton']",
			].join(",");

			return Boolean(element.closest(interactiveSelector));
		};

		const handleKeyDown = (e: KeyboardEvent) => {
			const isSpace = e.key === " " || e.code === "Space";
			const isArrowLeft = e.key === "ArrowLeft";
			const isArrowRight = e.key === "ArrowRight";

			if (!isSpace && !isArrowLeft && !isArrowRight) return;
			if (e.ctrlKey || e.metaKey || e.altKey) return;

			const targetElement = e.target as Element | null;
			const activeElement = document.activeElement;
			if (
				isEditableOrInteractiveElement(targetElement) ||
				isEditableOrInteractiveElement(activeElement)
			) {
				return;
			}

			e.preventDefault();
			if (isSpace) {
				togglePlay();
			} else if (isArrowLeft) {
				handleSkip(-10);
			} else if (isArrowRight) {
				handleSkip(10);
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [togglePlay, handleSkip]);

	const handleToggleFavorite = async () => {
		await toggleFavorite({
			sessionId: session.id,
			currentIsFavorite: isFavorite,
			onUpdated: (nextIsFavorite) => {
				isFavoriteRef.current = nextIsFavorite;
				setIsFavorite(nextIsFavorite);
				notifySessionUpdate({ isFavorite: nextIsFavorite });
			},
		});
	};

	const handleRename = () => {
		errorUI.showPrompt(
			"セッションの新しいタイトルを入力してください:",
			title,
			async (newTitle) => {
				if (newTitle.trim() !== "" && service) {
					const trimmedTitle = newTitle.trim();
					await service.renameSession(session.id, trimmedTitle);
					titleRef.current = trimmedTitle;
					setTitle(trimmedTitle);
					notifySessionUpdate({ title: trimmedTitle });
					errorUI.showNotification(
						"タイトルを更新しました。",
						ErrorSeverity.LOW,
					);
				}
			},
		);
	};

	const handleDelete = () => {
		if (session.id === currentSessionId) {
			errorUI.showNotification(
				"進行中のセッションは削除できません。終了してから削除してください。",
				ErrorSeverity.MEDIUM,
			);
			return;
		}

		errorUI.showConfirmation(
			"このセッションを削除してもよろしいですか？",
			async () => {
				if (service) {
					await service.deleteSession(session.id);
					onBack();
				}
			},
		);
	};

	const handleCopySummary = async () => {
		if (!summary) return;
		try {
			await navigator.clipboard.writeText(summary.overallSummary);
			errorUI.showNotification(
				"要約をクリップボードにコピーしました。",
				ErrorSeverity.LOW,
			);
		} catch (err) {
			console.error("Failed to copy summary:", err);
			errorUI.showNotification("コピーに失敗しました。", ErrorSeverity.MEDIUM);
		}
	};

	const handleRegenerate = async () => {
		if (!service) return;
		try {
			setIsRegenerating(true);
			const newSummary = await service.generateSummaryManual(session.id);
			setSummary(newSummary);
			errorUI.showNotification("要約を再生成しました。", ErrorSeverity.LOW);
		} catch (err) {
			errorUI.showNotification(
				`エラー: ${err instanceof Error ? err.message : "再生成に失敗しました"}`,
				ErrorSeverity.HIGH,
			);
		} finally {
			setIsRegenerating(false);
		}
	};

	const handleUpdateSegment = useCallback(
		async (id: string, newText: string) => {
			if (service) {
				try {
					await service.updateTranscriptSegment(session.id, id, newText);
					setTranscript((prev: typeof transcript) => {
						const nextTranscript = prev.map((s) =>
							s.id === id ? { ...s, text: newText } : s,
						);
						transcriptRef.current = nextTranscript;
						notifySessionUpdate({ transcript: nextTranscript });
						return nextTranscript;
					});
				} catch (err) {
					console.error("Failed to update segment", err);
					errorUI.showNotification(
						"文字起こしの更新に失敗しました。",
						ErrorSeverity.MEDIUM,
					);
				}
			}
		},
		[service, session.id, notifySessionUpdate],
	);

	const handleDeleteSegment = useCallback(
		async (id: string) => {
			errorUI.showConfirmation(
				"この発言を削除してもよろしいですか？",
				async () => {
					if (service) {
						try {
							await service.deleteTranscriptSegment(session.id, id);
							setTranscript((prev: typeof transcript) => {
								const nextTranscript = prev.filter((s) => s.id !== id);
								transcriptRef.current = nextTranscript;
								notifySessionUpdate({ transcript: nextTranscript });
								return nextTranscript;
							});
						} catch (err) {
							console.error("Failed to delete segment", err);
							errorUI.showNotification(
								"文字起こしの削除に失敗しました。",
								ErrorSeverity.MEDIUM,
							);
						}
					}
				},
			);
		},
		[service, session.id, notifySessionUpdate],
	);

	// Performance optimization: Pre-calculate the active segment ID to avoid
	// O(N) mapping and VDOM reconciliation of the entire transcript list
	// on every audio playback tick (timeUpdate).
	const isTranscriptSortedByStart = useMemo(() => {
		for (let i = 1; i < transcript.length; i++) {
			if (transcript[i - 1].start > transcript[i].start) return false;
		}
		return true;
	}, [transcript]);

	const activeSegmentId = useMemo(() => {
		const timeMs = currentTime * 1000;

		// transcript が非ソートの場合は線形探索へフォールバックする。
		if (!isTranscriptSortedByStart) {
			const active = transcript.find(
				(segment) => timeMs >= segment.start && timeMs < segment.end,
			);
			return active ? active.id || active.start : null;
		}

		// Performance optimization: Using binary search (O(log N)) for sorted transcripts.
		let low = 0;
		let high = transcript.length - 1;
		let active = null;

		while (low <= high) {
			const mid = (low + high) >>> 1;
			const segment = transcript[mid];

			if (timeMs >= segment.start && timeMs < segment.end) {
				active = segment;
				break;
			}

			if (timeMs < segment.start) {
				high = mid - 1;
			} else {
				low = mid + 1;
			}
		}

		return active ? active.id || active.start : null;
	}, [currentTime, transcript, isTranscriptSortedByStart]);

	// Auto-scroll to active segment during playback
	useEffect(() => {
		// `0` は有効な segment.start なので、falsy 判定ではなく nullish 判定を使う
		if (activeSegmentId != null && transcriptContainerRef.current) {
			const activeEl = transcriptContainerRef.current.querySelector(
				'[data-active="true"]',
			);
			if (activeEl && typeof activeEl.scrollIntoView === "function") {
				activeEl.scrollIntoView({
					block: "nearest",
					behavior: "smooth",
				});
			}
		}
	}, [activeSegmentId]);

	// Performance optimization: Memoize the entire transcript list JSX.
	// This prevents O(N) mapping and VDOM reconciliation on every audio playback tick.
	// Re-mapping only occurs when the active segment actually changes,
	// or when the transcript data/callbacks are updated.
	const transcriptList = useMemo(() => {
		return transcript.map((segment) => {
			const id = segment.id || segment.start;
			const isActive = activeSegmentId === id;
			return (
				<TranscriptRow
					key={id}
					segment={segment}
					isActive={isActive}
					onSeek={handleSeek}
					onUpdate={handleUpdateSegment}
					onDelete={handleDeleteSegment}
				/>
			);
		});
	}, [
		transcript,
		activeSegmentId,
		handleSeek,
		handleUpdateSegment,
		handleDeleteSegment,
	]);

	const exportMarkdown = (full: boolean) => {
		if (!summary) return;
		const transcriptText = full
			? transcript.map((s) => s.text).join("\n")
			: undefined;
		exporterRef.current.downloadAsFile(summary, transcriptText, {
			includeTranscript: full,
		});
	};

	const exportJson = () => {
		exporterRef.current.downloadAsJson(
			{
				...session,
				title,
				isFavorite,
				transcript,
			},
			summary,
		);
	};

	return (
		<div className="flex flex-col gap-6 h-full overflow-hidden">
			{/* Hidden audio element for stable playback */}
			{audioUrl && (
				// biome-ignore lint/a11y/useMediaCaption: Transcript is provided below the audio element as a functional equivalent
				<audio
					ref={audioRef}
					src={audioUrl}
					preload="auto"
					onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
					onDurationChange={(e) => {
						const d = e.currentTarget.duration;
						if (d !== Infinity && !Number.isNaN(d) && d > 0) {
							setDuration(d);
						}
					}}
					onPlay={() => setIsPlaying(true)}
					onPause={() => setIsPlaying(false)}
					onEnded={() => setIsPlaying(false)}
					onError={(e) => {
						console.error("Audio error event:", e);
						errorUI.showNotification(
							"音声の再生中にエラーが発生しました。",
							ErrorSeverity.MEDIUM,
						);
					}}
				/>
			)}

			<div className="flex items-center gap-4 flex-wrap">
				<button
					type="button"
					className="p-2 bg-bg-main border border-border-main rounded-lg cursor-pointer text-xl transition-all hover:border-text-dim focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none shrink-0 flex items-center justify-center active:scale-95"
					onClick={onBack}
					title="戻る"
					aria-label="戻る"
				>
					{UI_ICONS.BACK}
				</button>
				<div className="flex-1 min-w-0">
					<h3 className="m-0 text-xl font-bold">
						<button
							type="button"
							className="bg-transparent border-none text-left p-0 cursor-pointer text-text-main flex items-center gap-2 hover:text-primary transition-all w-full active:scale-95 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none rounded-md px-1 -mx-1"
							onClick={handleRename}
							aria-label="タイトルを編集"
						>
							<span className="truncate">{title || "無題のセッション"}</span>
							<span className="text-sm opacity-50 shrink-0">
								{UI_ICONS.EDIT}
							</span>
						</button>
					</h3>
					<div className="text-sm text-text-dim mt-1">
						{formatFullJapaneseDate(session.startTime)} •{" "}
						{Math.floor((session.metrics?.totalDuration || 0) / 60000)} min
					</div>
				</div>
				<div className="flex gap-2 shrink-0">
					<button
						type="button"
						className={`p-2 rounded-lg bg-bg-main border border-border-main transition-all cursor-pointer flex items-center justify-center text-xl hover:border-amber-500/50 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none active:scale-95 ${isFavorite ? "text-amber-500 border-amber-500/30" : "text-text-dim"}`}
						onClick={handleToggleFavorite}
						title={isFavorite ? "お気に入り解除" : "お気に入りに追加"}
						aria-label={isFavorite ? "お気に入り解除" : "お気に入りに追加"}
					>
						<span role="img" aria-hidden="true">
							{isFavorite
								? UI_ICONS.FAVORITE_ACTIVE
								: UI_ICONS.FAVORITE_INACTIVE}
						</span>
					</button>
					<button
						type="button"
						className={`p-2 rounded-lg bg-primary/10 border border-primary/20 text-primary transition-all flex items-center justify-center text-xl ${
							session.id === currentSessionId
								? "opacity-20 cursor-not-allowed"
								: "cursor-pointer hover:bg-primary hover:text-white focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none active:scale-95"
						}`}
						onClick={async () => {
							if (service && session.id !== currentSessionId) {
								await service.resumeExistingSession(session.id);
								if (onClose) onClose();
								onBack(); // Close detail view
							}
						}}
						disabled={session.id === currentSessionId}
						title={
							session.id === currentSessionId
								? "進行中のセッションです"
								: "このセッションを再開"
						}
						aria-label="セッションを再開"
					>
						<span role="img" aria-hidden="true">
							▶️
						</span>
					</button>
					<button
						type="button"
						className={`p-2 rounded-lg bg-bg-main border border-border-main transition-all flex items-center justify-center text-xl ${
							session.id === currentSessionId
								? "opacity-20 cursor-not-allowed text-text-dim"
								: "cursor-pointer text-text-dim hover:text-red-500 hover:border-red-500/30 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:outline-none active:scale-95"
						}`}
						onClick={handleDelete}
						disabled={session.id === currentSessionId}
						title={
							session.id === currentSessionId
								? "進行中のセッションは削除できません"
								: `セッション「${title || "無題"}」を削除`
						}
						aria-label={
							session.id === currentSessionId
								? "進行中のセッションは削除できません"
								: `セッション「${title || "無題"}」を削除`
						}
					>
						<span role="img" aria-hidden="true">
							{UI_ICONS.DELETE}
						</span>
					</button>
				</div>
			</div>

			<div className="flex gap-3 flex-wrap">
				<button
					type="button"
					className="bg-bg-main border border-border-main text-text-dim px-4 py-2 rounded-md cursor-pointer transition-all hover:border-text-dim hover:text-text-main focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none inline-flex items-center gap-2 text-sm disabled:opacity-50 active:scale-95"
					onClick={handleCopySummary}
					disabled={!summary}
					title="クリップボードにコピー"
				>
					{UI_ICONS.COPY} コピー
				</button>
				<button
					type="button"
					className="bg-bg-main border border-border-main text-text-dim px-4 py-2 rounded-md cursor-pointer transition-all hover:border-text-dim hover:text-text-main focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none inline-flex items-center gap-2 text-sm active:scale-95"
					onClick={() => exportMarkdown(false)}
				>
					{UI_ICONS.SUMMARY} 要約を保存
				</button>
				<button
					type="button"
					className="bg-bg-main border border-border-main text-text-dim px-4 py-2 rounded-md cursor-pointer transition-all hover:border-text-dim hover:text-text-main focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none inline-flex items-center gap-2 text-sm active:scale-95"
					onClick={() => exportMarkdown(true)}
				>
					{UI_ICONS.TRANSCRIPT} 全文を保存
				</button>
				<button
					type="button"
					className="bg-bg-main border border-border-main text-text-dim px-4 py-2 rounded-md cursor-pointer transition-all hover:border-text-dim hover:text-text-main focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none inline-flex items-center gap-2 text-sm active:scale-95"
					onClick={exportJson}
				>
					{UI_ICONS.SETTINGS} JSONを保存
				</button>
			</div>

			<div className="bg-bg-main border border-border-main rounded-xl p-4 flex items-center gap-4">
				<div className="flex items-center gap-2 shrink-0">
					<button
						type="button"
						className="w-10 h-10 rounded-full bg-white/5 text-text-main border border-border-main cursor-pointer flex items-center justify-center text-lg transition-all hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none disabled:opacity-30 disabled:cursor-not-allowed active:scale-95 relative"
						onClick={() => handleSkip(-10)}
						disabled={!audioUrl || duration === 0}
						title="10秒戻る (←)"
						aria-label="10秒戻る"
					>
						{UI_ICONS.SKIP_BACK}
						<kbd className="hidden md:inline-flex absolute -bottom-6 left-1/2 -translate-x-1/2 px-1.5 py-0.5 text-[10px] bg-white/20 rounded font-mono border-none whitespace-nowrap">
							←
						</kbd>
					</button>
					<button
						type="button"
						className="w-12 h-12 rounded-full bg-primary text-white border-none cursor-pointer flex items-center justify-center text-xl transition-all hover:bg-primary-hover focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg-main focus-visible:outline-none disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 relative"
						onClick={togglePlay}
						disabled={!audioUrl || duration === 0}
						title="再生/一時停止 (Space)"
						aria-label={isPlaying ? "一時停止" : "再生"}
					>
						{isPlaying ? UI_ICONS.PAUSE : UI_ICONS.PLAY}
						<kbd className="hidden md:inline-flex absolute -bottom-6 left-1/2 -translate-x-1/2 px-1.5 py-0.5 text-[10px] bg-white/20 rounded font-mono border-none whitespace-nowrap">
							Space
						</kbd>
					</button>
					<button
						type="button"
						className="w-10 h-10 rounded-full bg-white/5 text-text-main border border-border-main cursor-pointer flex items-center justify-center text-lg transition-all hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none disabled:opacity-30 disabled:cursor-not-allowed active:scale-95 relative"
						onClick={() => handleSkip(10)}
						disabled={!audioUrl || duration === 0}
						title="10秒進む (→)"
						aria-label="10秒進む"
					>
						{UI_ICONS.SKIP_FORWARD}
						<kbd className="hidden md:inline-flex absolute -bottom-6 left-1/2 -translate-x-1/2 px-1.5 py-0.5 text-[10px] bg-white/20 rounded font-mono border-none whitespace-nowrap">
							→
						</kbd>
					</button>
				</div>
				<div className="flex-1 flex items-center gap-3">
					<span className="text-xs font-mono text-text-dim w-10 text-right">
						{formatPlaybackTime(currentTime)}
					</span>
					<input
						type="range"
						className="flex-1 h-1.5 bg-border-main rounded-lg appearance-none cursor-pointer accent-primary"
						min="0"
						max={duration || 0}
						step="0.1"
						value={currentTime}
						onChange={(e) => {
							const time = parseFloat(e.target.value);
							if (audioRef.current) audioRef.current.currentTime = time;
							setCurrentTime(time);
						}}
						disabled={!audioUrl || duration === 0}
						aria-label="再生位置"
					/>
					<span className="text-xs font-mono text-text-dim w-10">
						{formatPlaybackTime(duration)}
					</span>
				</div>
			</div>

			<div className="flex flex-col gap-4">
				<div className="bg-bg-main border border-border-main rounded-xl overflow-hidden flex flex-col">
					<div className="px-4 py-3 border-b border-border-main flex justify-between items-center bg-white/5">
						<h4 className="m-0 text-sm font-bold flex items-center gap-2">
							📝 要約
						</h4>
						<button
							type="button"
							className="bg-transparent border border-border-main text-text-dim px-3 py-1 rounded text-xs cursor-pointer transition-all hover:text-text-main hover:border-text-dim focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none disabled:opacity-50 active:scale-95"
							disabled={isRegenerating}
							onClick={handleRegenerate}
						>
							{UI_ICONS.REGENERATE} {isRegenerating ? "処理中..." : "再生成"}
						</button>
					</div>
					<div className="p-5 text-sm leading-relaxed prose prose-invert max-w-none">
						{summary ? (
							<div
								// biome-ignore lint/security/noDangerouslySetInnerHtml: Sanitized by renderMarkdown
								dangerouslySetInnerHTML={{
									__html: renderedSummary,
								}}
							/>
						) : (
							<div className="text-text-dim italic opacity-60">
								(要約がありません)
							</div>
						)}
					</div>
				</div>
			</div>

			<div className="flex flex-col gap-3 min-h-0 flex-1">
				<div className="flex justify-between items-center px-1">
					<h4 className="m-0 text-sm font-bold flex items-center gap-2">
						📜 全文文字起こし
					</h4>
					<span className="text-[11px] text-text-dim opacity-60 italic">
						タップで再生位置を移動
					</span>
				</div>
				<div
					className="flex-1 overflow-y-auto bg-bg-main border border-border-main rounded-xl p-4 flex flex-col gap-2 scrollbar-thin scrollbar-thumb-white/10"
					ref={transcriptContainerRef}
				>
					{transcriptList}
				</div>
			</div>
		</div>
	);
};
